/**
 * MotsClesBand — keyword chip input.
 * On « Générer »: calls fromSeeds(brandId, seeds).
 * Proposals that have a text REPLACE Drafts.kw; others counted as dropped.
 * 429 GEN_COOLDOWN → calls onCooldown(until).
 *
 * @see B-popup-frontend.md step 5/6, Files and props § KeywordsBand
 */
import { useState, useRef } from 'react';
import { Box, Chip } from '@mui/material';
import { MAX_SEEDS, GEN_COOLDOWN_MS, cooldownSecondsLeft } from './composerModel';
import { GenerateButton } from './GenerateBand';
import { brandQueriesApi } from '../../services/brand-queries';

export interface MotsClesBandProps {
  keywords: string[];
  brandId: string;
  cooldownUntil: number | null;
  done: boolean;
  dropped: number;
  onChange: (keywords: string[]) => void;
  /** Called with proposal texts on success; parent updates draft + calls onVerifier. */
  onDraft: (lines: string[], dropped: number) => void;
  /** Called on 429 GEN_COOLDOWN with the new cooldownUntil timestamp. */
  onCooldown: (until: number) => void;
}

export function MotsClesBand({
  keywords,
  brandId,
  cooldownUntil,
  done,
  dropped,
  onChange,
  onDraft,
  onCooldown,
}: MotsClesBandProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // shared 1-s tick for cooldown display
  const [_now, setNow] = useState(() => Date.now());
  const left = cooldownSecondsLeft(cooldownUntil, _now);
  if (left > 0) setTimeout(() => setNow(Date.now()), 1000);

  const addKeyword = (raw: string) => {
    const trimmed = raw.trim().replace(/^#/, '').trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    if (keywords.some((k) => k.toLowerCase() === lower)) return;
    if (keywords.length >= MAX_SEEDS) return;
    onChange([...keywords, trimmed]);
    setInput('');
  };

  const removeKeyword = (kw: string) => {
    onChange(keywords.filter((k) => k !== kw));
  };

  const handleRun = async () => {
    if (loading || keywords.length === 0) return;
    setLoading(true);
    setError(false);
    try {
      const res = await brandQueriesApi.fromSeeds(brandId, keywords);
      onCooldown(Date.now() + GEN_COOLDOWN_MS);
      const proposals = res.proposals ?? [];
      const lines = proposals.filter((p) => p.text).map((p) => p.text!) as string[];
      const droppedCount = proposals.filter((p) => !p.text).length;
      onDraft(lines, droppedCount);
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; retryAfterSeconds?: number } };
      if (e?.body?.code === 'GEN_COOLDOWN' && typeof e.body.retryAfterSeconds === 'number') {
        onCooldown(Date.now() + e.body.retryAfterSeconds * 1000);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 0.5 }}>
      {/* Chip area */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          minHeight: 36,
          p: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          cursor: 'text',
        }}
        onClick={() => inputRef.current?.focus()}
      >
        {keywords.map((kw) => (
          <Chip
            key={kw}
            label={`#${kw}`}
            size="small"
            onDelete={() => removeKeyword(kw)}
            sx={{ height: 24, fontSize: 12, bgcolor: 'rgba(124,58,237,.08)' }}
          />
        ))}
        {keywords.length < MAX_SEEDS && (
          <Box
            component="input"
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addKeyword(input);
              } else if (e.key === 'Backspace' && !input && keywords.length > 0) {
                removeKeyword(keywords[keywords.length - 1]);
              }
            }}
            placeholder={keywords.length === 0 ? '+ mot clé' : '+'} // i18n
            sx={{
              border: 'none',
              outline: 'none',
              fontSize: 12,
              bgcolor: 'transparent',
              flex: 1,
              minWidth: 80,
              color: 'text.primary',
            }}
          />
        )}
      </Box>

      {/* Counter */}
      <Box sx={{ fontSize: 12, color: keywords.length >= MAX_SEEDS ? '#b45309' : 'text.disabled' }}>
        {/* i18n: promptStudio.kw.counter */}
        {keywords.length} / {MAX_SEEDS}
      </Box>

      {/* Generate button */}
      <GenerateButton
        cooldownUntil={cooldownUntil}
        done={done}
        loading={loading}
        onRun={handleRun}
      />

      {/* Dropped count */}
      {dropped > 0 && (
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
          {/* i18n: promptStudio.kw.dropped */}
          {dropped} mot{dropped > 1 ? 's clés' : ' clé'} sans proposition
        </Box>
      )}

      {error && (
        <Box sx={{ fontSize: 12, color: '#b45309' }}>
          {/* i18n: promptStudio.gen.failed */}
          La génération a échoué, réessayez.
        </Box>
      )}
    </Box>
  );
}
